import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, AlertCircle, Printer, Pencil } from "lucide-react";
import { inrShort } from "@/lib/api";
import { RecordDialog, COMPLAINT_FIELDS, SUGGESTION_FIELDS, UNHAPPY_FIELDS } from "@/components/QuickActions";

const dash = (n) => (n || n === 0 ? (n === 0 ? "--" : n) : "--");
const pct = (a, b) => (b ? Math.round((100 * a) / b) : 0);
const daysOpen = (c) => {
  const start = c.date ? new Date(c.date) : null;
  if (!start) return 0;
  const end = (["Resolved", "Closed"].includes(c.status) && c.resolved_at) ? new Date(c.resolved_at) : new Date();
  return Math.max(0, Math.floor((end - start) / 86400000));
};
const isResolved = (c) => ["Resolved", "Closed"].includes(c.status);
const isOverdue = (c) => !isResolved(c) && c.date && (Date.now() - new Date(c.date)) > 86400000;

const Stat = ({ label, value, accent = "#C9A24B", sub }) => (
  <Card className="p-4">
    <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</p>
    <p className="font-display text-2xl text-white font-light mt-1" style={{ color: accent }}>{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
  </Card>
);

export default function AdminActivity() {
  const { person, branch, dateRange, team } = useApp();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState([]);
  const [compFilter, setCompFilter] = useState("Open");
  const [sugFilter, setSugFilter] = useState("All");
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/daily-summary", { params: { person: person || "All", branch: branch || "All", date_from: dateRange.from, date_to: dateRange.to } });
      setData(res.data);
      const m = await api.get("/admin/missing-entries");
      setMissing(m.data.missing || []);
    } catch {}
  }, [person, branch, dateRange.from, dateRange.to]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const role = person ? (team.find((t) => t.name === person)?.role || "") : "All";
  const isMgr = role === "All" || role === "Operational Manager" || role === "Manager";
  const isExe = role === "All" || role === "Relationship Executive";
  const sum = (f) => (data.people || []).reduce((s, p) => s + (p[f] || 0), 0);
  const days = sum("days");

  const concerns = data.concerns || [];
  const complaints = concerns.filter((c) => c.concern_type !== "Unhappy Reason");
  const unhappy = concerns.filter((c) => c.concern_type === "Unhappy Reason");
  const suggestions = data.suggestions || [];
  const cReceived = complaints.length;
  const cResolved = complaints.filter(isResolved).length;
  const cOpen = cReceived - cResolved;
  const cOverdue = complaints.filter(isOverdue).length;
  const sReceived = suggestions.length;
  const sImpl = suggestions.filter((s) => s.status === "Implemented").length;

  const lfSum = ["lf_tv", "lf_music", "lf_fragrance", "lf_display", "lf_cleanliness", "lf_activity"].reduce((s, f) => s + sum(f), 0);
  const fbCats = [["fb_doctor", "Doctor"], ["fb_reception", "Reception"], ["fb_ot", "OT Staff"], ["fb_hygiene", "Hygiene"], ["fb_overall", "Overall"]];
  const fbSum = fbCats.reduce((s, [f]) => s + sum(f), 0);

  const printComplaints = () => {
    const rows = complaints.filter((c) => compFilter === "All" || (compFilter === "Resolved" ? isResolved(c) : !isResolved(c)));
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Complaints Report</title><style>body{font-family:sans-serif;padding:20px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:6px;font-size:12px;text-align:left}th{background:#eee}.od{color:#b91c1c;font-weight:bold}</style></head><body><h2>Complaints / Complications — ${person || "All Staff"} · ${dateRange.from} → ${dateRange.to}</h2><table><tr><th>Patient</th><th>Concern</th><th>Type</th><th>Responsible</th><th>Date</th><th>Days Open</th><th>Status</th></tr>${rows.map((c) => `<tr><td>${c.patient_name || "-"}</td><td>${c.concern_text || c.resolution_action || "-"}</td><td>${c.concern_type || "-"}</td><td>${c.responsible_person || "-"}</td><td>${(c.date || "").slice(0, 10)}</td><td class="${isOverdue(c) ? "od" : ""}">${daysOpen(c)}${isOverdue(c) ? " OVERDUE" : ""}</td><td>${c.status || "-"}</td></tr>`).join("")}</table></body></html>`);
    w.document.close(); w.print();
  };

  const filteredComplaints = complaints.filter((c) => compFilter === "All" || (compFilter === "Resolved" ? isResolved(c) : !isResolved(c)));
  const filteredSug = suggestions.filter((s) => sugFilter === "All" || s.status === sugFilter);
  const unhappyByCat = {};
  unhappy.forEach((u) => { const k = u.reason_category || "Other"; unhappyByCat[k] = (unhappyByCat[k] || 0) + 1; });

  return (
    <div className="mb-8" data-testid="admin-activity">
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-[#C9A24B]" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          {role === "All" ? "Team Activity" : `${role} Performance`} · {person || "All Staff"} · {branch || "All"} · {dateRange.from} → {dateRange.to}
        </span>
      </div>

      {missing.length > 0 && (
        <Card className="p-3 mb-4">
          <span className="text-xs text-slate-400">Aaj entry nahi bhari: </span>
          {missing.map((m) => <Badge key={m.name} data-testid="missing-person" className="ml-1 bg-[#DC2626]/15 text-[#f87171] border-0">{m.name} ({m.role || "no role"})</Badge>)}
        </Card>
      )}

      {/* MANAGER PERFORMANCE */}
      {isMgr && (
        <div data-testid="mgr-dashboard" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-4">
            <Stat label="Patients Visited" value={dash(sum("patients_visited"))} />
            <Stat label="New Patients" value={dash(sum("new_patients"))} />
            <Stat label="Wanted Treatment" value={dash(sum("wanted_treatment"))} />
            <Stat label="Took Treatment" value={dash(sum("took_treatment"))} accent="#34d399" />
            <Stat label="Counselled" value={dash(sum("total_counselled"))} />
            <Stat label="Conversion %" value={`${pct(sum("took_treatment"), sum("total_counselled"))}%`} accent="#34d399" />
            <Stat label="Referrals" value={dash(sum("referrals_count"))} />
            <Stat label="Cross-sell ₹" value={inrShort(sum("cross_sell_amount"))} accent="#34d399" />
            <Stat label="Reviews" value={dash(sum("reviews_count"))} />
            <Stat label="Testimonials" value={dash(sum("testimonials_count"))} />
            <Stat label="Look & Feel %" value={`${days ? pct(lfSum, 30 * days) : 0}%`} />
            <Stat label="Feedback Score %" value={`${days ? pct(fbSum, 25 * days) : 0}%`} accent="#34d399"
              sub={fbCats.map(([f, l]) => `${l} ${days ? (sum(f) / days).toFixed(1) : 0}`).join(" · ")} />
            <Stat label="Complaints Recd" value={dash(cReceived)} accent="#f87171" sub={`${cOpen} open · ${cOverdue} overdue`} />
            <Stat label="Resolution Rate %" value={`${pct(cResolved, cReceived)}%`} accent="#34d399" sub={`${cResolved} resolved`} />
            <Stat label="Suggestions" value={dash(sReceived)} sub={`${sImpl} implemented · ${pct(sImpl, sReceived)}%`} />
          </div>

          {/* Complaints list */}
          <Card className="overflow-hidden mb-4">
            <div className="flex items-center justify-between p-3 border-b border-white/5 flex-wrap gap-2">
              <span className="text-sm text-slate-200">Complaints / Complications</span>
              <div className="flex items-center gap-2">
                {["Open", "Resolved", "All"].map((f) => (
                  <button key={f} data-testid={`comp-filter-${f}`} onClick={() => setCompFilter(f)}
                    className={`px-2.5 py-1 rounded-md text-xs ${compFilter === f ? "bg-[#C9A24B] text-[#0F172A]" : "bg-[#0F172A] border border-white/10 text-slate-300"}`}>{f}</button>
                ))}
                <Button data-testid="print-complaints" size="sm" variant="ghost" onClick={printComplaints} className="text-slate-300 hover:text-white"><Printer className="h-4 w-4 mr-1" /> Print</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-white/5 hover:bg-transparent">
                  {["Patient", "Concern", "Type", "Responsible", "Date", "Days Open", "Status", ""].map((h) => <TableHead key={h} className="text-[10px] uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {filteredComplaints.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-6">--</TableCell></TableRow>}
                  {filteredComplaints.map((c) => (
                    <TableRow key={c.id} data-testid="complaint-row" className="border-white/5 text-sm text-slate-200">
                      <TableCell className="text-white">{c.patient_name || "--"}</TableCell>
                      <TableCell className="max-w-xs whitespace-normal text-slate-300">{c.concern_text || c.resolution_action || "--"}</TableCell>
                      <TableCell>{c.concern_type || "--"}</TableCell>
                      <TableCell>{c.responsible_person || "--"}</TableCell>
                      <TableCell className="font-mono-ync">{(c.date || "").slice(0, 10)}</TableCell>
                      <TableCell className={`font-mono-ync ${isOverdue(c) ? "text-[#f87171] font-bold" : ""}`}>{daysOpen(c)}{isOverdue(c) ? " · OVERDUE" : ""}</TableCell>
                      <TableCell><Badge className={`border-0 ${isResolved(c) ? "bg-[#059669]/15 text-[#34d399]" : "bg-[#DC2626]/15 text-[#f87171]"}`}>{c.status}</Badge></TableCell>
                      <TableCell><Button size="icon" variant="ghost" data-testid="edit-complaint" onClick={() => setEdit({ endpoint: "concerns", fields: COMPLAINT_FIELDS, title: "Edit Complaint", initial: c })} className="h-7 w-7 text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Suggestions list */}
          <Card className="overflow-hidden mb-4">
            <div className="flex items-center justify-between p-3 border-b border-white/5 flex-wrap gap-2">
              <span className="text-sm text-slate-200">Suggestions</span>
              <div className="flex items-center gap-2">
                {["All", "Not Implemented", "In Progress", "Implemented"].map((f) => (
                  <button key={f} data-testid={`sug-filter-${f.replace(/ /g, "-")}`} onClick={() => setSugFilter(f)}
                    className={`px-2.5 py-1 rounded-md text-xs ${sugFilter === f ? "bg-[#C9A24B] text-[#0F172A]" : "bg-[#0F172A] border border-white/10 text-slate-300"}`}>{f}</button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-white/5 hover:bg-transparent">
                  {["Patient", "Suggestion", "Date", "Status", "Remark", ""].map((h) => <TableHead key={h} className="text-[10px] uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {filteredSug.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">--</TableCell></TableRow>}
                  {filteredSug.map((s) => (
                    <TableRow key={s.id} data-testid="suggestion-row" className="border-white/5 text-sm text-slate-200">
                      <TableCell className="text-white">{s.patient_name || "--"}</TableCell>
                      <TableCell className="max-w-xs whitespace-normal text-slate-300">{s.suggestion_text || "--"}</TableCell>
                      <TableCell className="font-mono-ync">{(s.date || "").slice(0, 10)}</TableCell>
                      <TableCell><Badge className="border-0 bg-[#64748B]/20 text-slate-200">{s.status}</Badge></TableCell>
                      <TableCell className="text-slate-400">{s.remark || "--"}</TableCell>
                      <TableCell><Button size="icon" variant="ghost" data-testid="edit-suggestion" onClick={() => setEdit({ endpoint: "suggestions", fields: SUGGESTION_FIELDS, title: "Edit Suggestion", initial: s })} className="h-7 w-7 text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      )}

      {/* EXECUTIVE PERFORMANCE */}
      {isExe && (
        <div data-testid="exe-dashboard" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
            <Stat label="Calls Made" value={dash(sum("calls_made"))} />
            <Stat label="Follow-up Calls" value={dash(sum("followup_calls"))} />
            <Stat label="Connected" value={dash(sum("calls_connected"))} accent="#34d399" />
            <Stat label="Not Connected" value={dash(sum("calls_not_connected"))} accent="#f87171" />
            <Stat label="Connect %" value={`${pct(sum("calls_connected"), sum("calls_made"))}%`} accent="#34d399" />
            <Stat label="Due Recovered" value={inrShort(sum("due_recovered"))} accent="#34d399" />
            <Stat label="Revisits" value={dash(sum("revisit_patients"))} />
            <Stat label="Feedback" value={dash(sum("feedback_count"))} />
            <Stat label="Reviews" value={dash(sum("reviews_count"))} />
            <Stat label="Testimonials" value={dash(sum("testimonials_count"))} />
            <Stat label="Concerns" value={dash(sum("concerns_recorded"))} accent="#f87171" />
            <Stat label="Referrals" value={dash(sum("referrals_count"))} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            {(() => { const tot = sum("happy") + sum("satisfied") + sum("unhappy"); return (<>
              <Stat label="Happy" value={dash(sum("happy"))} accent="#34d399" sub={`${pct(sum("happy"), tot)}%`} />
              <Stat label="Satisfied" value={dash(sum("satisfied"))} accent="#C9A24B" sub={`${pct(sum("satisfied"), tot)}%`} />
              <Stat label="Unhappy" value={dash(sum("unhappy"))} accent="#f87171" sub={`${pct(sum("unhappy"), tot)}%`} />
              <Stat label="Total Responded" value={dash(tot)} />
            </>); })()}
          </div>
        </div>
      )}

          {/* Unhappy reasons */}
          <Card className="overflow-hidden mb-6" data-testid="unhappy-reasons-table">
            <div className="flex items-center justify-between p-3 border-b border-white/5 flex-wrap gap-2">
              <span className="text-sm text-slate-200">Unhappy Patient Reasons <span className="text-slate-500 text-xs">({unhappy.length})</span></span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(unhappyByCat).map(([k, v]) => <Badge key={k} className="bg-[#DC2626]/15 text-[#f87171] border-0">{k}: {v}</Badge>)}
                {unhappy.length < sum("unhappy") && <span className="text-xs text-[#C9A24B]">Hint: {sum("unhappy") - unhappy.length} unhappy without a reason logged</span>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-white/5 hover:bg-transparent">
                  {["Patient", "Reason Category", "Reason", "Status", ""].map((h) => <TableHead key={h} className="text-[10px] uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {unhappy.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">--</TableCell></TableRow>}
                  {unhappy.map((u) => (
                    <TableRow key={u.id} data-testid="unhappy-row" className="border-white/5 text-sm text-slate-200">
                      <TableCell className="text-white">{u.patient_name || "--"}</TableCell>
                      <TableCell>{u.reason_category || "--"}</TableCell>
                      <TableCell className="max-w-xs whitespace-normal text-slate-300">{u.concern_text || "--"}</TableCell>
                      <TableCell><Badge className="border-0 bg-[#64748B]/20 text-slate-200">{u.status}</Badge></TableCell>
                      <TableCell><Button size="icon" variant="ghost" data-testid="edit-unhappy" onClick={() => setEdit({ endpoint: "concerns", fields: UNHAPPY_FIELDS, title: "Edit Unhappy Reason", initial: u })} className="h-7 w-7 text-slate-400 hover:text-white"><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

      {/* ALL STAFF TABLE */}
      <div className="flex items-center gap-2 mb-2">
        <AlertCircle className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">All Staff — one row per person</span>
      </div>
      <Card className="overflow-hidden mb-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {["Person", "Role", "Days", "Visited", "Counselled", "Treatment", "Calls", "Follow-ups", "Due Rec.", "Revenue", "Upsell ₹", "Referrals"].map((h) => (
                  <TableHead key={h} className="text-[10px] uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.people.length === 0 && <TableRow><TableCell colSpan={12} className="text-center text-slate-500 py-8">--</TableCell></TableRow>}
              {data.people.map((p) => (
                <TableRow key={p.person} data-testid="activity-row" className="border-white/5 hover:bg-[#0F172A]/40 text-slate-200 text-sm">
                  <TableCell className="text-white">{p.person}</TableCell>
                  <TableCell className="text-slate-400">{p.role}</TableCell>
                  <TableCell className="font-mono-ync">{p.days}</TableCell>
                  <TableCell className="font-mono-ync">{p.patients_visited || 0}</TableCell>
                  <TableCell className="font-mono-ync">{p.total_counselled || p.patients_counselled || 0}</TableCell>
                  <TableCell className="font-mono-ync">{p.took_treatment || p.patients_treatment || 0}</TableCell>
                  <TableCell className="font-mono-ync">{p.calls_made || 0}</TableCell>
                  <TableCell className="font-mono-ync">{p.followup_calls || 0}</TableCell>
                  <TableCell className="font-mono-ync text-[#34d399]">{inrShort(p.due_recovered || 0)}</TableCell>
                  <TableCell className="font-mono-ync text-[#34d399]">{inrShort(p.revenue_generated || 0)}</TableCell>
                  <TableCell className="font-mono-ync">{inrShort(p.upsell_amount || 0)}</TableCell>
                  <TableCell className="font-mono-ync">{p.referrals_count || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {edit && (
        <RecordDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit.title}
          endpoint={edit.endpoint} fieldDefs={edit.fields} initial={edit.initial}
          onSaved={() => { setEdit(null); load(); }} />
      )}
    </div>
  );
}
